import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Select,
  Space,
  Table,
  Tag,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import {
  type PublicRoomConfiguration,
  type PublicRoomStatus,
  publicRoomsApi,
  type SavePublicRoomConfiguration,
} from '@/services/publicRoomsApi';

interface FormValues {
  roomId: string;
  displayName: string;
  tagline: string;
  description: string;
  highlightsText: string;
  sortOrder: number;
  defaultSessionId: string;
}

const PAGE_SIZE = 20;
const statusLabel: Record<PublicRoomStatus, string> = {
  draft: '草稿',
  published: '已上架',
  unpublished: '已下架',
};

function toFormValues(room?: PublicRoomConfiguration): FormValues {
  return {
    roomId: room?.roomId ?? '',
    displayName: room?.displayName ?? '',
    tagline: room?.tagline ?? '',
    description: room?.description ?? '',
    highlightsText: room?.highlights.join('\n') ?? '',
    sortOrder: room?.sortOrder ?? 0,
    defaultSessionId: room?.defaultSessionId ?? '',
  };
}

function toSaveInput(values: FormValues): SavePublicRoomConfiguration {
  return {
    roomId: values.roomId.trim(),
    displayName: values.displayName.trim(),
    tagline: values.tagline.trim(),
    description: values.description?.trim() ?? '',
    highlights: (values.highlightsText ?? '')
      .split('\n')
      .map((item) => item.trim())
      .filter(Boolean),
    sortOrder: values.sortOrder ?? 0,
    defaultSessionId: values.defaultSessionId?.trim() ?? '',
  };
}

function errorText(error: unknown, fallback: string) {
  if (!error || typeof error !== 'object') return fallback;
  const value = error as {
    message?: string;
    response?: { data?: { message?: string } };
  };
  return value.response?.data?.message || value.message || fallback;
}

export function PublicRoomsPage() {
  const [form] = Form.useForm<FormValues>();
  const [rows, setRows] = useState<PublicRoomConfiguration[]>([]);
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [searchDraft, setSearchDraft] = useState('');
  const [offset, setOffset] = useState(0);
  const [nextOffset, setNextOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingRoomId, setPendingRoomId] = useState('');
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<
    PublicRoomConfiguration | null | undefined
  >();
  const [preview, setPreview] = useState<FormValues>(toFormValues());

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await publicRoomsApi.list({
        status: status || undefined,
        search: search || undefined,
        pageSize: PAGE_SIZE,
        offset,
      });
      setRows(result.rooms ?? []);
      setNextOffset(result.nextOffset ?? 0);
    } catch (cause) {
      setError(errorText(cause, '公共房间列表加载失败'));
    } finally {
      setLoading(false);
    }
  }, [offset, search, status]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') void load();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [load]);

  const openForm = (room?: PublicRoomConfiguration) => {
    setEditing(room ?? null);
    const values = toFormValues(room);
    form.setFieldsValue(values);
    setPreview(values);
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      const input = toSaveInput(values);
      if (input.highlights.length > 6) {
        message.warning('亮点最多填写 6 条');
        return;
      }
      setSaving(true);
      if (editing) await publicRoomsApi.update(input);
      else await publicRoomsApi.create(input);
      message.success(editing ? '展示信息已更新' : '房间已关联为草稿');
      setEditing(undefined);
      await load();
    } catch (cause) {
      if (cause && typeof cause === 'object' && 'errorFields' in cause) return;
      message.error(errorText(cause, '保存失败，请稍后重试'));
    } finally {
      setSaving(false);
    }
  };

  const setPublished = async (
    room: PublicRoomConfiguration,
    published: boolean,
  ) => {
    setPendingRoomId(room.roomId);
    try {
      if (published) await publicRoomsApi.publish(room.roomId);
      else await publicRoomsApi.unpublish(room.roomId);
      message.success(published ? '已上架' : '已下架，原成员关系保留');
      await load();
    } catch (cause) {
      message.error(errorText(cause, published ? '上架失败' : '下架失败'));
    } finally {
      setPendingRoomId('');
    }
  };

  return (
    <div className="space-y-5 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">公共房间</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            关联已准备好的真实房间，配置场景卡片，再上架到
            Tutti。下架保留房间内容和已有成员。
          </p>
        </div>
        <Button type="primary" onClick={() => openForm()}>
          添加已有房间
        </Button>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          action={
            <Button size="small" onClick={() => void load()}>
              重试
            </Button>
          }
        />
      )}
      <Space wrap>
        <Input.Search
          value={searchDraft}
          onChange={(event) => setSearchDraft(event.target.value)}
          onSearch={(value) => {
            setOffset(0);
            setSearch(value.trim());
          }}
          placeholder="搜索名称或 Room ID"
          allowClear
          style={{ width: 280 }}
        />
        <Select
          value={status}
          onChange={(value) => {
            setOffset(0);
            setStatus(value);
          }}
          style={{ width: 140 }}
          options={[
            { value: '', label: '全部状态' },
            { value: 'draft', label: '草稿' },
            { value: 'published', label: '已上架' },
            { value: 'unpublished', label: '已下架' },
          ]}
        />
        <Button onClick={() => void load()} loading={loading}>
          刷新
        </Button>
      </Space>

      <Table<PublicRoomConfiguration>
        rowKey="roomId"
        loading={loading}
        dataSource={rows}
        pagination={false}
        locale={{ emptyText: '暂无公共房间' }}
        columns={[
          {
            title: '场景',
            key: 'scene',
            render: (_, room) => (
              <div>
                <div className="font-medium">{room.displayName}</div>
                <div className="text-sm text-muted-foreground">
                  {room.tagline}
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                  {room.roomId}
                </div>
              </div>
            ),
          },
          {
            title: '状态',
            key: 'status',
            render: (_, room) => (
              <Space direction="vertical" size={2}>
                <Tag
                  color={
                    room.status === 'published'
                      ? 'green'
                      : room.status === 'draft'
                        ? 'blue'
                        : 'default'
                  }
                >
                  {statusLabel[room.status]}
                </Tag>
                {!room.sourceAvailable && <Tag color="red">来源不可用</Tag>}
                {room.sourceAvailable && !room.shareAvailable && (
                  <Tag color="orange">分享条件不满足</Tag>
                )}
              </Space>
            ),
          },
          { title: '排序', dataIndex: 'sortOrder', width: 90 },
          {
            title: '默认 Session',
            dataIndex: 'defaultSessionId',
            width: 180,
            render: (value: string) => value || '—',
          },
          {
            title: '最近修改',
            dataIndex: 'updatedAtUnixMs',
            width: 175,
            render: (value: number) =>
              value ? new Date(value).toLocaleString() : '—',
          },
          {
            title: '操作',
            key: 'actions',
            width: 220,
            render: (_, room) => (
              <Space wrap>
                <Button size="small" onClick={() => openForm(room)}>
                  编辑
                </Button>
                {room.status === 'published' ? (
                  <Popconfirm
                    title="下架此公共房间？"
                    description="公共目录和新的公共入口将关闭，已有成员保留。"
                    onConfirm={() => void setPublished(room, false)}
                  >
                    <Button
                      size="small"
                      danger
                      loading={pendingRoomId === room.roomId}
                    >
                      下架
                    </Button>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="上架此公共房间？"
                    description="上架后所有用户可以从公共目录加入。"
                    onConfirm={() => void setPublished(room, true)}
                  >
                    <Button
                      size="small"
                      type="primary"
                      disabled={!room.sourceAvailable || !room.shareAvailable}
                      loading={pendingRoomId === room.roomId}
                    >
                      上架
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
      />
      <Space>
        <Button
          disabled={offset === 0 || loading}
          onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
        >
          上一页
        </Button>
        <span>第 {Math.floor(offset / PAGE_SIZE) + 1} 页</span>
        <Button
          disabled={!nextOffset || loading}
          onClick={() => setOffset(nextOffset)}
        >
          下一页
        </Button>
      </Space>

      <Modal
        title={editing ? '编辑公共房间' : '添加已有房间'}
        open={editing !== undefined}
        onCancel={() => {
          if (!saving) setEditing(undefined);
        }}
        onOk={() => void save()}
        okText="保存"
        okButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
        width={700}
      >
        <Form<FormValues>
          form={form}
          layout="vertical"
          onValuesChange={(_, all) =>
            setPreview({ ...toFormValues(editing ?? undefined), ...all })
          }
        >
          <Form.Item
            name="roomId"
            label="已有 Room ID"
            rules={[{ required: true, whitespace: true }]}
          >
            <Input
              disabled={Boolean(editing)}
              placeholder="先在 Tutti 创建房间并准备内容"
            />
          </Form.Item>
          <Form.Item
            name="displayName"
            label="展示名称"
            rules={[{ required: true, whitespace: true, max: 80 }]}
          >
            <Input maxLength={80} />
          </Form.Item>
          <Form.Item
            name="tagline"
            label="一句话介绍"
            rules={[{ required: true, whitespace: true, max: 160 }]}
          >
            <Input maxLength={160} />
          </Form.Item>
          <Form.Item
            name="description"
            label="场景说明"
            rules={[{ max: 2000 }]}
          >
            <Input.TextArea rows={3} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item name="highlightsText" label="亮点（每行一条，最多 6 条）">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Space align="start" size={16}>
            <Form.Item
              name="sortOrder"
              label="排序值（越小越靠前）"
              rules={[{ required: true }]}
            >
              <InputNumber min={0} precision={0} />
            </Form.Item>
            <Form.Item name="defaultSessionId" label="默认 Session ID（可选）">
              <Input
                placeholder="只能选择本房间非私密根 Session"
                style={{ width: 290 }}
              />
            </Form.Item>
          </Space>
        </Form>
        <div className="rounded-lg border p-4">
          <div className="text-xs text-muted-foreground">卡片预览</div>
          <div className="mt-2 font-medium">
            {preview.displayName || '展示名称'}
          </div>
          <div className="text-sm text-muted-foreground">
            {preview.tagline || '一句话介绍'}
          </div>
          <p className="mt-2 text-sm">{preview.description}</p>
          <Space wrap>
            {preview.highlightsText
              ?.split('\n')
              .filter(Boolean)
              .slice(0, 6)
              .map((text) => (
                <Tag key={text}>{text}</Tag>
              ))}
          </Space>
        </div>
      </Modal>
    </div>
  );
}
